import React, { useEffect, useRef, useState } from 'react'
import 'react-dates/initialize'
import { DayPickerRangeController } from 'react-dates'
import moment from 'moment'
import { Button, FlashMessage } from '@reactiveonline/reactive_admin_shared-packages'

const PRESET_OPTIONS = [30, 60, 90, 'custom']

function presetLabel(option) {
  if (option === 'custom') return Partners.translations.reports.custom_range

  return Partners.translations.reports[`last_${ option }_days`]
}

function presetDescription(option) {
  if (option === 'custom') return Partners.translations.reports.pick_exact_range

  return Partners.translations.reports.rolling_window.replace('%{days}', option)
}

function normalizeRange(range, fallbackDays) {
  if (range?.start_date && range?.end_date) {
    return {
      start_date: moment(range.start_date),
      end_date: moment(range.end_date)
    }
  }

  return {
    start_date: moment().subtract(fallbackDays, 'days'),
    end_date: moment()
  }
}

function detectPreset(range) {
  const days = range.end_date.clone().startOf('day').diff(range.start_date.clone().startOf('day'), 'days')

  if ([30, 60, 90].includes(days)) {
    return days
  }

  return 'custom'
}

export default function PresetDateRangePicker({
  allowFutureDates = false,
  defaultPresetDays = 30,
  maxDateRangeDays = 92,
  onApply,
  pastDaysLimit,
  value
}) {
  const initialRange = normalizeRange(value, defaultPresetDays)
  const [selectedPreset, setSelectedPreset] = useState(detectPreset(initialRange))
  const [pendingRange, setPendingRange] = useState(initialRange)
  const [focusedInput, setFocusedInput] = useState('startDate')
  const [isOpen, setIsOpen] = useState(false)
  const flashMessageRef = useRef(null)
  const wrapperRef = useRef(null)
  const smallDevice = window.matchMedia('(max-width: 480px)').matches
  const upperRangeLimitMs = pastDaysLimit && (pastDaysLimit + 1) * 24 * 60 * 60 * 1000

  useEffect(() => {
    const nextRange = normalizeRange(value, defaultPresetDays)
    setPendingRange(nextRange)
    setSelectedPreset(detectPreset(nextRange))
  }, [value?.start_date, value?.end_date, defaultPresetDays])

  useEffect(() => {
    function handleOutsideClick(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false)
        setFocusedInput('startDate')
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  useEffect(() => {
    if (!(isOpen && selectedPreset === 'custom')) return

    const timer = window.requestAnimationFrame(() => {
      setFocusedInput('startDate')
    })

    return () => {
      window.cancelAnimationFrame(timer)
    }
  }, [isOpen, selectedPreset])

  function handlePresetClick(option) {
    setSelectedPreset(option)

    if (option === 'custom') {
      setIsOpen(true)
      return
    }

    setPendingRange({
      start_date: moment().subtract(option, 'days'),
      end_date: moment()
    })
    setFocusedInput('startDate')
  }

  function handleDateChange({ startDate, endDate }) {
    setPendingRange({
      start_date: startDate ? moment(startDate) : null,
      end_date: endDate ? moment(endDate) : null
    })
  }

  function handleApply() {
    setFocusedInput('startDate')

    if (!pendingRange.start_date || !pendingRange.end_date) return

    const rangeLength = pendingRange.end_date.clone().startOf('day').diff(pendingRange.start_date.clone().startOf('day'), 'days')
    if (rangeLength > maxDateRangeDays) {
      flashMessageRef.current.show(ReactiveCore.translations.reports.too_long_date_range, 'error')
      return
    }

    onApply({
      start_date: pendingRange.start_date.clone(),
      end_date: pendingRange.end_date.clone()
    })
    setIsOpen(false)
  }

  function selectedLabel() {
    if (selectedPreset !== 'custom') return presetLabel(selectedPreset)

    if (!pendingRange.start_date || !pendingRange.end_date) return presetLabel('custom')

    return `${ pendingRange.start_date.format('DD MMM YYYY') } - ${ pendingRange.end_date.format('DD MMM YYYY') }`
  }

  return (
    <div ref={ wrapperRef } className='preset-date-range'>
      <FlashMessage ref={ flashMessageRef } />

      <button
        type='button'
        className={ `preset-date-range-trigger${ isOpen ? ' active' : '' }` }
        onClick={ () => setIsOpen(current => !current) }
      >
        <span className='preset-date-range-trigger-copy'>
          <span className='preset-date-range-trigger-label'>{ Partners.translations.reports.date_range }</span>
          <span className='preset-date-range-trigger-value'>{ selectedLabel() }</span>
        </span>
        <i className={ `fa-light fa-chevron-${ isOpen ? 'up' : 'down' }` } />
      </button>

      { isOpen &&
        <div className='preset-date-range-popover'>
          <div className='preset-date-range-header'>
            <div>
              <div className='preset-date-range-eyebrow'>{ Partners.translations.reports.quick_select }</div>
              <div className='preset-date-range-title'>{ Partners.translations.reports.choose_reporting_window }</div>
            </div>

            <div className='preset-date-range-summary'>
              { selectedLabel() }
            </div>
          </div>

          <div className='preset-date-range-controls'>
            { PRESET_OPTIONS.map(option => (
              <button
                key={ option }
                type='button'
                className={ `preset-date-range-option${ selectedPreset === option ? ' active' : '' }` }
                onClick={ () => handlePresetClick(option) }
              >
                <span className='preset-date-range-option-title'>{ presetLabel(option) }</span>
                <span className='preset-date-range-option-description'>{ presetDescription(option) }</span>
              </button>
            )) }
          </div>

          { selectedPreset === 'custom' &&
            <div className='table-date-range preset-date-range-custom'>
              <DayPickerRangeController
                endDate={ pendingRange.end_date }
                focusedInput={ focusedInput }
                onDatesChange={ handleDateChange }
                onFocusChange={ nextFocusedInput => setFocusedInput(nextFocusedInput || 'startDate') }
                numberOfMonths={ smallDevice ? 1 : 2 }
                startDate={ pendingRange.start_date }
                isOutsideRange={ date => ((!allowFutureDates && date > new Date() - 1) || (pastDaysLimit && date < new Date() - upperRangeLimitMs)) }
                minimumNights={ 0 }
                firstDayOfWeek={ 1 }
                hideKeyboardShortcutsPanel
                daySize={ smallDevice ? 44 : 48 }
              />
            </div>
          }

          <div className='preset-date-range-apply'>
            <Button
              value={ ReactiveCore.translations.apply }
              className='btn'
              onClick={ handleApply }
            />
          </div>
        </div>
      }
    </div>
  )
}
